import { useState } from 'react';
import { useAuth } from '@/core/auth';
import { Receipt, ShieldCheck } from 'lucide-react';
import { Button, Card, Checkbox, SelectField, SettingsSection, SettingToggleCard } from '@/design-system';
import type { FinancialPolicies, FinancialPolicyInput } from '../../api/financial-policies.service';
import { useFinancialPolicies } from '../../hooks/useFinancialPolicies';

export function FinancialPoliciesSection() {
  const { user } = useAuth();
  const allowed = ['SUPERADMIN', 'FINANCE'].includes(user?.role ?? '');
  const { query, save } = useFinancialPolicies(allowed);
  if (!allowed) return <Card><p>Somente Financeiro e Superadmin podem acessar as políticas financeiras.</p></Card>;
  if (query.isPending) return <Card><p>Carregando políticas financeiras…</p></Card>;
  if (query.isError || !query.data) return <Card><p role="alert">Não foi possível carregar as políticas financeiras.</p><Button onClick={() => void query.refetch()}>Tentar novamente</Button></Card>;
  return <FinancialPolicyEditor key={`${user?.id}:${user?.role}:${query.data.version}`} data={query.data}
    saving={save.isPending} onSave={input => save.mutate(input)} onReload={() => void query.refetch()} />;
}

function FinancialPolicyEditor({ data, saving, onSave, onReload }: {
  data: FinancialPolicies; saving: boolean; onSave: (input: FinancialPolicyInput) => void; onReload: () => void;
}) {
  const [effectiveAt, setEffectiveAt] = useState(data.changeEffectiveAt);
  const [affiliatesEnabled, setAffiliatesEnabled] = useState(data.affiliatesEnabled ?? false);
  const [prorate, setProrate] = useState(data.prorate);
  return <div className="space-y-6">
    <SettingToggleCard title="Ativar programa de afiliados" icon={ShieldCheck}
      description="Quando desativado, não é possível criar, ativar ou utilizar cupons vinculados a afiliados."
      checked={affiliatesEnabled} onChange={setAffiliatesEnabled} disabled={saving} />
    <SettingsSection title="Cobrança" icon={Receipt} description="Orientações para o controle manual. Estas políticas não executam pagamentos nem alteram assinaturas nos gateways.">
      <div className="space-y-4 pt-4">
        <div className="grid gap-4 md:grid-cols-2">
          <SelectField label="Modo de cobrança" value="MANUAL" disabled onChange={() => undefined}
            options={[{ value: 'MANUAL', label: 'Manual' }]} />
          <SelectField label="Vigência financeira padrão das alterações" value={effectiveAt} disabled={saving}
            onChange={(value) => setEffectiveAt(value as FinancialPolicies['changeEffectiveAt'])}
            options={[{ value: 'NEXT_RENEWAL', label: 'Próxima renovação' }, { value: 'IMMEDIATE', label: 'Imediata' }]} />
        </div>
        <Checkbox label="Orientar o financeiro a calcular o valor proporcional ao período" checked={prorate} disabled={saving} onChange={event => setProrate(event.target.checked)} />
        <p className="rounded-lg bg-primary/5 p-3 text-sm text-gray-600">O acesso aos módulos e o limite de usuários continuam sendo atualizados imediatamente ao alterar o plano. A vigência acima orienta somente o financeiro; não agenda cobranças.</p>
        <p className="text-xs text-gray-500">{data.updatedAt ? `Última alteração: ${new Date(data.updatedAt).toLocaleString('pt-BR')} · revisão ${data.version}` : 'Política inicial sugerida. Salve para confirmar a orientação da plataforma.'}</p>
      </div>
    </SettingsSection>
    <div className="flex justify-end gap-2"><Button variant="outline" disabled={saving} onClick={onReload}>Recarregar políticas</Button>
      <Button isLoading={saving} disabled={saving} onClick={() => onSave({ billingMode: 'MANUAL', changeEffectiveAt: effectiveAt, prorate, affiliatesEnabled, expectedVersion: data.version })}>Salvar políticas</Button></div>
  </div>;
}
